/**
 * TableSkeleton - Skeleton loading state for tables
 *
 * Features:
 * - Configurable rows and columns
 * - Header skeleton option
 * - No layout shift on load
 */
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

export interface TableSkeletonProps {
  /** Number of rows to display */
  rows?: number;
  /** Number of columns to display */
  columns?: number;
  /** Whether to show header skeleton */
  showHeader?: boolean;
  /** Custom column widths (percentages or fixed) */
  columnWidths?: string[];
  /** Additional class name */
  className?: string;
}

export function TableSkeleton({
  rows = 5,
  columns = 4,
  showHeader = true,
  columnWidths,
  className,
}: TableSkeletonProps) {
  const getColumnWidth = (index: number) => {
    if (columnWidths && columnWidths[index]) {
      return columnWidths[index];
    }
    return undefined;
  };

  return (
    <div className={cn('rounded-md border', className)}>
      <Table>
        {showHeader && (
          <TableHeader>
            <TableRow>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <TableHead
                  key={colIndex}
                  style={{ width: getColumnWidth(colIndex) }}
                >
                  <Skeleton className="h-4 w-20" />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
        )}
        <TableBody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <TableRow key={rowIndex}>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <TableCell
                  key={colIndex}
                  style={{ width: getColumnWidth(colIndex) }}
                >
                  <Skeleton
                    className={cn(
                      'h-4',
                      colIndex === 0 ? 'w-32' : colIndex === columns - 1 ? 'w-16' : 'w-24'
                    )}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * DataTableSkeleton - Skeleton for data tables with actions
 */
export interface DataTableSkeletonProps {
  /** Number of rows to display */
  rows?: number;
  /** Whether to show search input skeleton */
  showSearch?: boolean;
  /** Whether to show filter buttons skeleton */
  showFilters?: boolean;
  /** Whether to show pagination skeleton */
  showPagination?: boolean;
  /** Additional class name */
  className?: string;
}

export function DataTableSkeleton({
  rows = 5,
  showSearch = true,
  showFilters = true,
  showPagination = true,
  className,
}: DataTableSkeletonProps) {
  return (
    <div className={cn('space-y-4', className)}>
      {/* Toolbar */}
      {(showSearch || showFilters) && (
        <div className="flex items-center justify-between gap-4">
          {showSearch && (
            <Skeleton className="h-10 w-[250px]" />
          )}
          {showFilters && (
            <div className="flex items-center gap-2">
              <Skeleton className="h-10 w-[100px]" />
              <Skeleton className="h-10 w-[100px]" />
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <TableSkeleton rows={rows} columns={5} />

      {/* Pagination */}
      {showPagination && (
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-[200px]" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-8 w-8" />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * ListSkeleton - Skeleton for simple list views
 */
export interface ListSkeletonProps {
  /** Number of items to display */
  items?: number;
  /** Whether items have avatars */
  showAvatar?: boolean;
  /** Whether items have descriptions */
  showDescription?: boolean;
  /** Additional class name */
  className?: string;
}

export function ListSkeleton({
  items = 5,
  showAvatar = false,
  showDescription = true,
  className,
}: ListSkeletonProps) {
  return (
    <div className={cn('space-y-4', className)}>
      {Array.from({ length: items }).map((_, index) => (
        <div key={index} className="flex items-center gap-4 p-4 border rounded-lg">
          {showAvatar && (
            <Skeleton className="h-10 w-10 rounded-full" />
          )}
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-[200px]" />
            {showDescription && (
              <Skeleton className="h-3 w-[300px]" />
            )}
          </div>
          <Skeleton className="h-8 w-[80px]" />
        </div>
      ))}
    </div>
  );
}
